import React from 'react';
import { styled, css } from '@compiled/react';
// @ts-expect-error - TS2614 - Module '"@atlaskit/dynamic-table"' has no exported member 'RowType'. Did you mean to use 'import RowType from "@atlaskit/dynamic-table"' instead? | TS2614 - Module '"@atlaskit/dynamic-table"' has no exported member 'HeadType'. Did you mean to use 'import HeadType from "@atlaskit/dynamic-table"' instead?
import type { RowType, HeadType } from '@atlaskit/dynamic-table';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl';
import { ISSUE_TABLE_ROW_INNER_HEIGHT } from '../../common/constants';
import type { Deployment } from '../../common/types';
import { useHoveredDeployment } from '../../controllers/hovered-item';
import { JiraIssuesPerDeploymentCellContent } from './jira-issues-per-deployment-cell-content';
import messages from './messages';

export const useTableHeader = (): HeadType => {
	const { formatMessage } = useIntl();
	return {
		cells: [
			{
				key: 'jiraIssuesPerDeployment',
				content: formatMessage(messages.jiraIssuePerDeploymentColumnHead),
				isSortable: true,
				width: 180,
			},
			{
				key: 'deployment',
				content: formatMessage(messages.deploymentColumnHead),
				isSortable: true,
				width: 140,
			},
			{
				key: 'environmentName',
				content: formatMessage(messages.environmentNameColumnHead),
				isSortable: true,
				width: 200,
			},
			{
				key: 'pipeline',
				content: formatMessage(messages.pipelineColumnHead),
				isSortable: true,
				width: 360,
			},
			{
				key: 'dateDeployed',
				content: formatMessage(messages.dateDeployedColumnHead),
				isSortable: true,
				width: 100,
			},
		],
	};
};

export const useTableRows = (
	data: Deployment[] | null | undefined,
	medianIssuesPerDeployment: number | null | null | undefined,
	selectDeployment: (arg1: Deployment) => void,
): RowType[] | null => {
	const { formatDate } = useIntl();
	const [, actions] = useHoveredDeployment();

	if (data == null || medianIssuesPerDeployment === undefined) {
		return null;
	}

	if (medianIssuesPerDeployment === null) {
		return [];
	}

	return data.map((deployment: Deployment) => {
		const {
			id,
			issueCount,
			displayName: deploymentName,
			url: deploymentUrl,
			environment: { displayName: environmentName },
			lastUpdated: dateDeployed,
			pipeline: { url: pipelineUrl, displayName: pipelineName },
		} = deployment;
		return {
			'data-deployment-id': id,
			// @ts-expect-error - TS7006 - Parameter 'evt' implicitly has an 'any' type.
			onMouseEnter: (evt) => {
				actions.setHoveredDeployment(evt.currentTarget.dataset.deploymentId);
			},
			onMouseLeave: () => {
				actions.setHoveredDeployment(null);
			},
			cells: [
				{
					key: `${issueCount} ${id}`,
					content: (
						<JiraIssuesPerDeploymentCellContent
							medianIssuesPerDeployment={medianIssuesPerDeployment}
							issueCount={issueCount}
							onClick={() => selectDeployment(deployment)}
						/>
					),
				},
				{
					key: `${deploymentName} ${id}`,
					content: (
						<CellContentLink href={deploymentUrl} target="_blank">
							{deploymentName}
						</CellContentLink>
					),
				},
				{
					key: `${environmentName} ${id}`,
					content: <CellContent>{environmentName}</CellContent>,
				},
				{
					key: `${pipelineName} ${id}`,
					content: (
						<CellContentLink href={pipelineUrl} target="_blank">
							{pipelineName}
						</CellContentLink>
					),
				},
				{
					// @ts-expect-error - TS2339 - Confusion between JS Date and GraphQL Date scalar
					key: `${dateDeployed.getTime()} ${id}`,
					content: (
						<Tooltip
							content={formatDate(dateDeployed, {
								year: 'numeric',
								month: 'long',
								day: 'numeric',
								hour: 'numeric',
								minute: 'numeric',
							})}
							position="top-start"
						>
							<CellContent>
								{formatDate(dateDeployed, {
									month: 'short',
									day: 'numeric',
								})}
							</CellContent>
						</Tooltip>
					),
				},
			],
		};
	});
};

const contentCommonCssStyles = css({
	textOverflow: 'ellipsis',
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${ISSUE_TABLE_ROW_INNER_HEIGHT}px`,
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const CellContent = styled.div(contentCommonCssStyles);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const CellContentLink = styled.a(contentCommonCssStyles, {
	textDecoration: 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.link', colors.B400),
	cursor: 'pointer',
	'&:hover': {
		textDecoration: 'none',
	},
});
